import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Card } from "@/components/ui/card";
import { Loader2, Mail, Plus, X, Bell, ChevronDown, ChevronUp } from "lucide-react";

export interface EmailConfig {
  email: string;
  enabled: boolean;
  days_before: number;
  early_pay_only: boolean;
  min_days_early: number;
  summary_email: boolean;
  /** Optional per-recipient minimum invoice amount threshold */
  min_amount?: number;
}

export interface NotificationSettings {
  days_before: number;
  enabled: boolean;
  notification_email: string;
  notification_emails: string[];
  early_pay_only: boolean;
  min_days_early: number;
  summary_email: boolean;
  email_configs: EmailConfig[];
}

interface NotificationSettingsModalProps {
  open: boolean;
  onClose: () => void;
  settings: NotificationSettings;
  saving: boolean;
  onSave: (settings: Partial<NotificationSettings>) => void;
}

function EmailConfigCard({
  config,
  onChange,
  onRemove,
}: {
  config: EmailConfig;
  onChange: (c: EmailConfig) => void;
  onRemove: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card className="px-3 py-2 space-y-2">
      <div className="flex items-center justify-between">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1.5 text-left flex-1 min-w-0"
        >
          <Mail className="w-3 h-3 text-muted-foreground shrink-0" />
          <span className="text-xs font-medium truncate">{config.email}</span>
          {expanded ? (
            <ChevronUp className="w-3 h-3 text-muted-foreground shrink-0" />
          ) : (
            <ChevronDown className="w-3 h-3 text-muted-foreground shrink-0" />
          )}
        </button>
        <div className="flex items-center gap-1.5 shrink-0 ml-2">
          <Switch
            checked={config.enabled}
            onCheckedChange={(v) => onChange({ ...config, enabled: v })}
          />
          <button
            onClick={onRemove}
            className="text-muted-foreground hover:text-destructive transition-colors p-0.5"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>

      {expanded && config.enabled && (
        <div className="space-y-3 pt-1 border-t border-border">
          <div className="space-y-1">
            <Label className="text-[11px] font-medium">
              Notify <span className="text-primary font-bold">{config.days_before}</span> day
              {config.days_before !== 1 ? "s" : ""} before deadline
            </Label>
            <Slider
              value={[config.days_before]}
              onValueChange={([v]) => onChange({ ...config, days_before: v })}
              min={1}
              max={14}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between text-[9px] text-muted-foreground">
              <span>1 day</span>
              <span>14 days</span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label className="text-[11px] font-medium">Early pay eligible only</Label>
              <p className="text-[9px] text-muted-foreground">
                Only notify for invoices with discount terms
              </p>
            </div>
            <Switch
              checked={config.early_pay_only}
              onCheckedChange={(v) => onChange({ ...config, early_pay_only: v })}
            />
          </div>

          <div className="space-y-1">
            <Label className="text-[11px] font-medium">
              Min days early:{" "}
              <span className="text-primary font-bold">{config.min_days_early || "Any"}</span>
            </Label>
            <Slider
              value={[config.min_days_early]}
              onValueChange={([v]) => onChange({ ...config, min_days_early: v })}
              min={0}
              max={30}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between text-[9px] text-muted-foreground">
              <span>Any</span>
              <span>30 days</span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label className="text-[11px] font-medium">Daily summary</Label>
              <p className="text-[9px] text-muted-foreground">
                Single daily digest instead of individual alerts
              </p>
            </div>
            <Switch
              checked={config.summary_email}
              onCheckedChange={(v) => onChange({ ...config, summary_email: v })}
            />
          </div>
        </div>
      )}

      {expanded && !config.enabled && (
        <p className="text-[10px] text-muted-foreground pt-1 border-t border-border">
          Notifications disabled for this recipient. Toggle on to configure.
        </p>
      )}
    </Card>
  );
}

export function NotificationSettingsModal({
  open,
  onClose,
  settings,
  saving,
  onSave,
}: NotificationSettingsModalProps) {
  const [emailConfigs, setEmailConfigs] = useState<EmailConfig[]>([]);
  const [newEmail, setNewEmail] = useState("");

  useEffect(() => {
    if (open) {
      if (settings.email_configs?.length) {
        setEmailConfigs(
          settings.email_configs.map((c) => ({
            email: c.email,
            enabled: c.enabled ?? true,
            days_before: c.days_before ?? 3,
            early_pay_only: c.early_pay_only ?? false,
            min_days_early: c.min_days_early ?? 0,
            summary_email: c.summary_email ?? false,
          }))
        );
      } else {
        const emails = settings.notification_emails?.length
          ? settings.notification_emails
          : settings.notification_email
            ? [settings.notification_email]
            : [];
        setEmailConfigs(
          emails.map((email) => ({
            email,
            enabled: settings.enabled,
            days_before: settings.days_before,
            early_pay_only: settings.early_pay_only,
            min_days_early: settings.min_days_early,
            summary_email: settings.summary_email,
          }))
        );
      }
    }
  }, [open, settings]);

  const addEmail = () => {
    const trimmed = newEmail.trim().toLowerCase();
    if (trimmed && trimmed.includes("@") && !emailConfigs.some((c) => c.email === trimmed)) {
      setEmailConfigs([
        ...emailConfigs,
        {
          email: trimmed,
          enabled: true,
          days_before: 3,
          early_pay_only: false,
          min_days_early: 0,
          summary_email: false,
        },
      ]);
      setNewEmail("");
    }
  };

  const removeEmail = (email: string) => {
    setEmailConfigs(emailConfigs.filter((c) => c.email !== email));
  };

  const updateConfig = (index: number, config: EmailConfig) => {
    const next = [...emailConfigs];
    next[index] = config;
    setEmailConfigs(next);
  };

  const handleSave = () => {
    const emails = emailConfigs.map((c) => c.email);
    const anyEnabled = emailConfigs.some((c) => c.enabled);
    onSave({
      enabled: anyEnabled,
      days_before: emailConfigs[0]?.days_before ?? 3,
      notification_email: emails[0] || "",
      notification_emails: emails,
      email_configs: emailConfigs,
      early_pay_only: emailConfigs[0]?.early_pay_only ?? false,
      min_days_early: emailConfigs[0]?.min_days_early ?? 0,
      summary_email: emailConfigs[0]?.summary_email ?? false,
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Bell className="w-4 h-4 text-primary" />
            Early Payment Notifications
          </DialogTitle>
          <DialogDescription className="text-xs">
            Add recipients and configure individual alert preferences
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label className="text-xs font-medium flex items-center gap-1.5">
              <Mail className="w-3 h-3" />
              Recipients
            </Label>
            <p className="text-[10px] text-muted-foreground">
              Each recipient has independent settings. Click to expand and configure.
            </p>

            <div className="space-y-1.5">
              {emailConfigs.map((config, i) => (
                <EmailConfigCard
                  key={config.email}
                  config={config}
                  onChange={(c) => updateConfig(i, c)}
                  onRemove={() => removeEmail(config.email)}
                />
              ))}
            </div>

            <div className="flex gap-1.5">
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addEmail())}
                placeholder="Add email address…"
                className="h-8 text-xs flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 px-2"
                onClick={addEmail}
                disabled={!newEmail.includes("@")}
              >
                <Plus className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          <Button
            onClick={handleSave}
            disabled={saving || emailConfigs.length === 0}
            className="w-full h-9 text-xs"
          >
            {saving && <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />}
            Save Settings
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
